import { lazy, Suspense, useState } from "react";
import { Routes, Route } from "react-router-dom";
import LayoutDecider from "./layoutDecider";
import Loading from "./_loading";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import PrivateRoute from "./privateRoute";
import NoMatch from "./404";
import Unauthorized from "./403";
import FrontLayout from "./frontLayout";
import RoleBasedRedirect from "../../constants/RoleBasedRedirect";
import { ToastContainer } from "react-toastify";

const Login = lazy(() => import("../login"));
const ForgetPassword = lazy(() => import("../forgotPassword"));
const Dashboard = lazy(() => import("../admin/dashboard"));
const User = lazy(() => import("../admin/user"));
const UserList = lazy(() => import("../admin/userlist"));
const AuthorityUserList = lazy(() => import("../admin/authorityuser"));
const Profile = lazy(() => import("../profile"));
const ChangePassword = lazy(() => import("../changePassword"));
const PasswordReset = lazy(() => import("../passwordReset"));
const AuthorityList = lazy(() => import("../admin/authoritylist"));
const AuthorityUser = lazy(() => import("../admin/addauthority"));
const BasicDetail = lazy(() => import("../authority/basicDetail"));
const CaseManagerList = lazy(() => import("../authority/CaseManagerList"));
const Authoritylist = lazy(() => import("../authority/authorityUserList"));
const ActionDiaryList = lazy(() => import("../authority/ActionDiary/ActionDiaryList"));
const ActionItemText = lazy(() => import("../authority/ActionDiary/ActionItemText"));
const ContractorList = lazy(() => import("../admin/contractorList"));
const Contractor = lazy(() => import("../admin/contractor"));
const DashboardCaseManager = lazy(() => import("../authority/DashboardCaseManager"));
const ActionDiary = lazy(() => import("../authority/ActionDiary/ActionDiary"));
const ActionFileUpload = lazy(() => import("../authority/ActionDiary/ActionFileUpload"));
const VerifyAccount = lazy(() => import("../verifyAccount"));

const SuspenseWrap = ({ children }) => <Suspense fallback={<Loading />}>{children}</Suspense>;

const ReCaptchaWrap = ({ children }) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={process.env.REACT_APP_GOOGLERECAPCHA_KEY}
    scriptProps={{ defer: true, async: true }}>
    {children}
  </GoogleReCaptchaProvider>
);

const Router = () => {
  const [role] = useState("AUTHORITY");

  return (
    <>
      <Routes>
        <Route path="/" element={<RoleBasedRedirect role={role} />} />

        <Route element={<FrontLayout />}>
          <Route
            path="/login"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <Login />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <ForgetPassword />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />

          <Route
            path="/verify-account"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <VerifyAccount />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />

          <Route
            path="/password-reset"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <PasswordReset />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />
        </Route>

        {/* ---------- Protected Pages ---------- */}
        <Route element={<LayoutDecider />}>
          <Route
            path="/change-password"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/profile"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/dashboard"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <Dashboard />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/admin"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <UserList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/authority"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <AuthorityList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/authority/contractor/list/:authorityId"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <ContractorList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/:clienttype/:contractor/list/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <Contractor />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/:clienttype/user/:authorityId?"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <AuthorityUserList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/:clienttype/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute role="ADMIN">
                  <User />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/:clienttype/user/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <AuthorityUser />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/authority/dashboard"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <DashboardCaseManager />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/authority/case/list"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <CaseManagerList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/case-manager/:type/:id?/:authorityId?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <BasicDetail />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/authority/user"
            element={
              <SuspenseWrap>
                <PrivateRoute role="AUTHORITY">
                  <Authoritylist />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/actionDiary/list/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <ActionDiaryList />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/actionDiary/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <ActionDiary />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/actionItemText/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <ActionItemText />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route
            path="/actionItemFile/:type/:id?"
            element={
              <SuspenseWrap>
                <PrivateRoute>
                  <ActionFileUpload />
                </PrivateRoute>
              </SuspenseWrap>
            }
          />

          <Route path="*" element={<NoMatch />} />
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NoMatch />} />
      </Routes>

      <ToastContainer />
    </>
  );
};

export default Router;
